import { useEffect, useRef, useState, useCallback } from "react";
import { X, RotateCcw, Check, SwitchCamera, Circle, Square, ImageIcon, AlertTriangle } from "lucide-react";

const MAX_SECONDS = 60;

function pickMime() {
  const cands = ["video/mp4;codecs=h264,aac", "video/mp4", "video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"];
  for (const m of cands) if (window.MediaRecorder && MediaRecorder.isTypeSupported(m)) return m;
  return "";
}

function detectError(e) {
  const name = e?.name || "";
  if (name === "NotAllowedError") return { title: "Camera/mic access denied", msg: "Please allow camera and microphone access, then try again." };
  if (name === "NotFoundError") return { title: "No camera found", msg: "This device does not appear to have a camera." };
  if (name === "NotReadableError") return { title: "Camera is in use", msg: "Another app is using the camera. Close it and try again." };
  if (name === "SecurityError" || name === "TypeError") return { title: "Camera blocked by browser", msg: "Camera access requires a secure (HTTPS) connection." };
  return { title: "Recording unavailable", msg: e?.message || "Unable to start recording." };
}

export default function VideoCapture({ onClose, onCapture, onFallback, maxSeconds = MAX_SECONDS }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const [facing, setFacing] = useState("environment");
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [preview, setPreview] = useState(null); // {blob, url, ext}
  const [err, setErr] = useState(null);
  const [starting, setStarting] = useState(true);
  const [ready, setReady] = useState(false);

  const stopStream = useCallback(() => {
    try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch {}
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const start = useCallback(async (mode) => {
    setStarting(true); setReady(false); setErr(null);
    stopStream();
    if (typeof navigator === "undefined" || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setErr({ title: "Recording not supported", msg: "This browser doesn't support video recording." });
      setStarting(false);
      return;
    }
    try {
      let s;
      try {
        s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: mode } }, audio: true });
      } catch (e1) {
        if (e1?.name === "OverconstrainedError" || e1?.name === "NotFoundError") {
          s = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        } else { throw e1; }
      }
      streamRef.current = s;
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        videoRef.current.muted = true;
        videoRef.current.setAttribute("playsinline", "true");
        try { await videoRef.current.play(); } catch {}
      }
      setReady(true);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Video start failed:", e);
      setErr(detectError(e));
    } finally {
      setStarting(false);
    }
  }, [stopStream]);

  useEffect(() => {
    start(facing);
    return () => { stopStream(); clearInterval(timerRef.current); };
  }, [facing, start, stopStream]);

  const startRec = () => {
    if (!streamRef.current) return;
    const mime = pickMime();
    try {
      chunksRef.current = [];
      const rec = new MediaRecorder(streamRef.current, mime ? { mimeType: mime } : undefined);
      rec.ondataavailable = (e) => e.data && e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime || "video/webm" });
        const ext = (blob.type.includes("mp4") ? "mp4" : "webm");
        setPreview({ blob, url: URL.createObjectURL(blob), ext });
      };
      rec.start(200);
      recorderRef.current = rec;
      setRecording(true); setSeconds(0);
      timerRef.current = setInterval(() => setSeconds(s => {
        const n = s + 1;
        if (n >= maxSeconds) stopRec();
        return n;
      }), 1000);
    } catch (e) {
      setErr({ title: "Recording not supported", msg: "This browser does not support MediaRecorder." });
    }
  };

  const stopRec = () => {
    clearInterval(timerRef.current);
    try { recorderRef.current && recorderRef.current.state !== "inactive" && recorderRef.current.stop(); } catch {}
    setRecording(false);
  };

  const accept = () => {
    if (!preview) return;
    const type = preview.blob.type || (preview.ext === "mp4" ? "video/mp4" : "video/webm");
    const file = new File([preview.blob], `video-${Date.now()}.${preview.ext}`, { type });
    stopStream();
    onCapture(file, "video_camera");
    URL.revokeObjectURL(preview.url);
  };

  const retake = () => { if (preview) URL.revokeObjectURL(preview.url); setPreview(null); setSeconds(0); };
  const handleClose = () => { stopRec(); stopStream(); onClose(); };

  const fmt = (s) => `${String(Math.floor(s/60)).padStart(2,'0')}:${String(s%60).padStart(2,'0')}`;

  return (
    <div className="fixed inset-0 z-[60] bg-black" data-testid="video-modal">
      <div className="absolute inset-0 flex flex-col">
        <div className="p-4 flex justify-between items-center text-white">
          <button onClick={handleClose} className="glass-subtle rounded-full p-2" data-testid="video-close"><X className="w-5 h-5" /></button>
          <div className="text-xs uppercase tracking-widest opacity-80">Record a video</div>
          <button onClick={() => setFacing(f => f === "environment" ? "user" : "environment")} disabled={recording || !ready} className="glass-subtle rounded-full p-2 disabled:opacity-40" data-testid="video-switch"><SwitchCamera className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 relative overflow-hidden grid place-items-center bg-black">
          {starting && !err && <div className="text-white/80 text-sm animate-pulse">Requesting camera…</div>}
          {err && (
            <div className="glass p-6 max-w-sm mx-4 text-center" data-testid="video-error">
              <AlertTriangle className="w-6 h-6 mx-auto mb-2" style={{ color: "var(--accent)" }} />
              <div className="font-serif text-2xl mb-2">{err.title}</div>
              <p className="text-neutral-700 text-sm">{err.msg}</p>
              <div className="mt-4 flex flex-wrap gap-2 justify-center">
                <button onClick={() => start(facing)} className="rounded-full px-5 py-2 text-sm text-white" style={{ background: "var(--accent)" }} data-testid="video-try-again">Try Again</button>
                {onFallback && (
                  <button onClick={() => { stopStream(); onFallback(); }} className="btn-ghost-glass rounded-full px-5 py-2 text-sm inline-flex items-center gap-2" data-testid="video-fallback">
                    <ImageIcon className="w-4 h-4" /> Choose from Gallery
                  </button>
                )}
                <button onClick={handleClose} className="btn-ghost-glass rounded-full px-5 py-2 text-sm" data-testid="video-error-close">Close</button>
              </div>
            </div>
          )}
          {!err && !preview && <video ref={videoRef} playsInline muted autoPlay className="w-full h-full object-cover" data-testid="video-live" />}
          {preview && <video src={preview.url} controls playsInline className="w-full h-full object-contain" data-testid="video-preview" />}
          {recording && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center gap-2 glass-subtle px-4 py-2 text-white" data-testid="video-timer">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-sm tabular-nums">{fmt(seconds)} / {fmt(maxSeconds)}</span>
            </div>
          )}
        </div>
        {!err && (
          <div className="p-6 flex justify-center items-center gap-6 bg-black/60">
            {!preview && !recording && (
              <button onClick={startRec} disabled={!ready} className="w-20 h-20 rounded-full border-4 border-white bg-red-500 grid place-items-center disabled:opacity-40" data-testid="video-start"><Circle className="w-8 h-8 text-white" fill="white" /></button>
            )}
            {recording && (
              <button onClick={stopRec} className="w-20 h-20 rounded-full border-4 border-white bg-red-500 grid place-items-center" data-testid="video-stop"><Square className="w-8 h-8 text-white" fill="white" /></button>
            )}
            {preview && !recording && (
              <>
                <button onClick={retake} className="glass-subtle rounded-full px-5 py-3 text-white flex items-center gap-2" data-testid="video-retake"><RotateCcw className="w-4 h-4" /> Retake</button>
                <button onClick={accept} className="rounded-full px-6 py-3 text-white flex items-center gap-2" style={{ background: "var(--accent)" }} data-testid="video-accept"><Check className="w-4 h-4" /> Use video</button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
